import { useState } from 'react';

import ExportIcon from '~/shared/assets/icons/export.svg';
import FilterButton from '~/shared/components/filters/FilterButton';
import Button from '~/shared/components/ButtonsV2';
import { SelectOption, Account } from '~/shared/utils/types';
import { useExportState } from '~/shared/context/ExportStateContext';

import FleetManagerFiltersSidePanel from '../AllVolumesFiltersSidePanel/AllVolumesFiltersSidePanel';
import VolumeViewDropdown from '~/shared/components/filters/VolumeViewDropdown';
import { VolumeViewPresetId } from '~/shared/utils/volumeViewPresets';
import VolumesExportModal from './VolumesExportModal';

interface AllVolumesTableHeaderProps {
  dataCount: number | undefined;
  selectedCategoryCount: number | undefined;
  activeFilterCount: number;
  sidePanelOpen: boolean;
  onTogglePanel: () => void;
  onSetPanelOpen?: (open: boolean) => void;
  onResetFilters: () => void;
  onSelectVolumeViewPreset: (presetId: VolumeViewPresetId) => void;
  appliedClusterNameFilters: SelectOption[];
  appliedInstanceFilters: SelectOption[];
  appliedVolumeIdFilters: SelectOption[];
  appliedVolumeTagFilters: SelectOption[];
  appliedAccountFilters: SelectOption[];
  appliedAutoscaleState: string[];
  appliedEligibility: string[];
  isOrgView?: boolean;
  accounts?: Account[];
  activeOrgId?: string;
  activeFilters: SelectOption[];
  presetCounts: Partial<Record<VolumeViewPresetId, number>>;
  presetCountsLoading: boolean;
  sortField: { field: string; dir: 'desc' | 'asc' };
}

export function AllVolumesTableHeader({
  dataCount,
  selectedCategoryCount,
  activeFilterCount,
  sidePanelOpen,
  onTogglePanel,
  onSetPanelOpen,
  onResetFilters,
  onSelectVolumeViewPreset,
  appliedClusterNameFilters,
  appliedInstanceFilters,
  appliedVolumeIdFilters,
  appliedVolumeTagFilters,
  appliedAccountFilters,
  appliedAutoscaleState,
  appliedEligibility,
  isOrgView = false,
  accounts = [],
  activeOrgId,
  activeFilters,
  presetCounts,
  presetCountsLoading,
  sortField,
}: AllVolumesTableHeaderProps) {
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const { isExporting } = useExportState();
  const volumesExporting = isExporting('volumes');

  return (
    <div className="flex justify-between pt-3 4xl:pt-[18px]">
      <div className="flex items-center gap-7 ">
        <VolumeViewDropdown
          appliedAutoscaleState={appliedAutoscaleState}
          appliedEligibility={appliedEligibility}
          onSelectPreset={onSelectVolumeViewPreset}
          presetCounts={presetCounts}
          isLoading={presetCountsLoading}
          selectedCount={selectedCategoryCount}
        />
      </div>
      <div className="flex gap-2 items-center justify-end w-full max-w-[713px]">
        <FilterButton
          activeFilterCount={activeFilterCount}
          onOpen={onTogglePanel}
          onClearAll={onResetFilters}
          size="md-responsive"
          variant="third"
        />
        <Button
          variant={'third'}
          onClick={() => {
            if (!volumesExporting) setIsExportModalOpen(true);
          }}
          className="w-10"
          size="md-responsive"
          loading={volumesExporting}
          disabled={volumesExporting}
          icon={<ExportIcon className="size-5" />}
        />
      </div>

      <VolumesExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        activeFilters={activeFilters}
        sortField={sortField}
        totalCount={dataCount}
      />
      <FleetManagerFiltersSidePanel
        open={sidePanelOpen}
        onOpenChange={(open: boolean) => {
          if (onSetPanelOpen) {
            onSetPanelOpen(open);
          } else {
            // Fallback: toggle only when change differs to avoid accidental close/open loops
            if (open !== sidePanelOpen) {
              onTogglePanel();
            }
          }
        }}
        onResetFilters={onResetFilters}
        appliedClusterNameFilters={appliedClusterNameFilters}
        appliedInstanceFilters={appliedInstanceFilters}
        appliedVolumeIdFilters={appliedVolumeIdFilters}
        appliedVolumeTagFilters={appliedVolumeTagFilters}
        appliedAccountFilters={appliedAccountFilters}
        appliedAutoscaleState={appliedAutoscaleState}
        appliedEligibility={appliedEligibility}
        isOrgView={isOrgView}
        accounts={accounts}
        activeOrgId={activeOrgId}
      />
    </div>
  );
}
